import { useCallback, useEffect, useState } from 'react'
import { fetchRoomSnapshot, subscribeToRoom, type RoomSnapshot } from '../lib/api/rooms'
import { supabase } from '../lib/supabase'
import type { RoomRow } from '../lib/database.types'
import { TvJoinBadge } from './TvJoinBadge'
import { TVLobbyPhase } from './phases/TVLobbyPhase'
import { TVBuildingPhase } from './phases/TVBuildingPhase'
import { TVPresentingPhase } from './phases/TVPresentingPhase'
import { TVResultsPhase } from './phases/TVResultsPhase'
import { useT } from '../i18n'

interface Props { code: string }

/**
 * Big-screen TV display for a party room. We pull a full snapshot
 * (room + players + submissions + votes) on every realtime tick, then
 * route to the right phase view based on `room.status`.
 *
 * The TV is a read-only mirror — the host drives the game from their
 * phone (same model as the Colorful and Three Step TV displays).
 */
export function TVRoomScreen({ code }: Props) {
  const t = useT()
  const [snapshot, setSnapshot] = useState<RoomSnapshot | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async (roomId: string) => {
    const snap = await fetchRoomSnapshot(roomId)
    if (snap) setSnapshot(snap)
  }, [])

  // Look up the room by code once, then subscribe to realtime updates.
  useEffect(() => {
    let cancelled = false
    let unsubscribe: (() => void) | null = null

    async function boot() {
      const { data, error: err } = await supabase
        .from('rooms').select('*').eq('code', code).maybeSingle()
      if (cancelled) return
      if (err) { setError(err.message); return }
      if (!data) { setError(t('Room not found. It may have ended.')); return }

      const initial = data as RoomRow
      await refetch(initial.id)
      if (cancelled) return

      unsubscribe = subscribeToRoom(initial.id, () => { refetch(initial.id) })
    }
    boot()
    return () => { cancelled = true; unsubscribe?.() }
  }, [code, refetch])

  if (error) {
    return (
      <TVShell>
        <h1 className="font-display text-6xl font-bold mb-4">{t('No such room')}</h1>
        <p className="text-2xl text-brand-muted">{error}</p>
        <a href="/tv" className="mt-8 inline-block text-brand-primary underline text-xl">{t('Enter another code')}</a>
      </TVShell>
    )
  }

  if (!snapshot) {
    return (
      <TVShell>
        <p className="text-2xl text-brand-muted">{t('Loading room…')}</p>
      </TVShell>
    )
  }

  const { room } = snapshot

  // Phase-route. Each child is fully responsible for its own layout;
  // TVShell just provides the full-screen safe-area frame.
  return (
    <TVShell>
      {room.status === 'lobby'      && <TVLobbyPhase snapshot={snapshot} />}
      {(room.status === 'building' ||
        room.status === 'dealing')  && <TVBuildingPhase snapshot={snapshot} />}
      {(room.status === 'presenting' ||
        room.status === 'voting')   && <TVPresentingPhase snapshot={snapshot} />}
      {room.status === 'results'    && <TVResultsPhase snapshot={snapshot} />}
      {room.status === 'abandoned'  && (
        <div className="text-center">
          <h1 className="font-display text-6xl font-bold mb-4">{t("Party's over")}</h1>
          <p className="text-2xl text-brand-muted">{t('Everyone left the room.')}</p>
          <a href="/tv" className="mt-8 inline-block text-brand-primary underline text-xl">{t('Enter another code')}</a>
        </div>
      )}
      {room.status !== 'abandoned' && <TvJoinBadge code={room.code} />}
    </TVShell>
  )
}

function TVShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg p-12">
      {children}
    </div>
  )
}
